import { generatePath, useHistory } from 'react-router-dom';

import { CustomField } from './components/CustomField';
import { CustomField as CustomFieldType } from 'features/CustomFields/types';
import { CustomFieldsComponentProps } from './types';
import { EVENT_MODE_NORMAL } from 'utils/constants';
import { EmptySection } from '../EmptySection';
import { ROUTES } from 'features/routes';
import { Wrapper } from './CustomFieldsComponent.styles';
import { toastError } from 'hooks/useToastNotification/useToastNotification';
import { useGetCustomFields } from './useGetCustomFields';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useTranslation } from 'react-i18next';
import {
	PunchupSingleColumnTileGrid,
	PunchupEntityTile,
} from 'features/common/components/Punchup/primitives/display/PunchupTile';
import { getCustomFieldName } from 'utils/utils';

export const CustomFieldsComponent = ({ eventId, eventMode, onEdit }: CustomFieldsComponentProps) => {
	const { t } = useTranslation();
	const { push } = useHistory();
	const { event } = useGetEvent();
	const { customFields } = useGetCustomFields();

	const text = t(`dashboard-${eventMode === EVENT_MODE_NORMAL ? 'custom-fields' : 'registration'}-info`);

	const onClickAddNew = () => {
		if (event?.ticketTypes?.length && event?.ticketTypes?.length > 0) {
			push(generatePath(ROUTES.DASHBOARD_ADD_CUSTOM_FIELDS, { eventId }));
		} else {
			toastError(t('dashboard-custom-fields-no-ticket-types'));
		}
	};

	if (!customFields || customFields.length === 0) {
		if (onEdit) return null; // parent (CustomFieldsSection) renders empty state in accordion mode
		return <EmptySection onClick={onClickAddNew} text={text} />;
	}

	// Accordion / punchup mode: render light-theme PunchupEntityTile cards (single column — questions can be long)
	if (onEdit) {
		return (
			<PunchupSingleColumnTileGrid>
				{customFields.map((field: CustomFieldType) => (
					<PunchupEntityTile
						key={field.ID}
						title={field.only_buyer ? t('customFields-buyerOnly') : t('customFields-eachAttendee')}
						name={getCustomFieldName(field.name)}
						descriptions={[field.is_required ? t('customFields-required') : t('customFields-notRequired')]}
						tagRow={
							field.ticket_types && field.ticket_types.length > 0
								? { label: 'Ticket types', tags: field.ticket_types.map((tt) => tt.name) }
								: undefined
						}
						onClick={() => onEdit(field.ID)}
					/>
				))}
			</PunchupSingleColumnTileGrid>
		);
	}

	// Standalone / dark-theme mode: original card list
	return (
		<Wrapper>
			{customFields.map((field: CustomFieldType) => (
				<CustomField key={field.ID} {...field} />
			))}
		</Wrapper>
	);
};
